import { Avatar, Box, Card, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import type { KnownNotificationResource } from '../../../api/notifications'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { mediaSourceUrl } from './utils'
import { StackedAvatars } from '../../ui-kit'
import type { IProjectAuthorShort } from '../../../api/types/projects'
import { createNotificationNavHandlers, getActorPathKey } from './notificationNav'
import { ActorNameSpan } from './notificationTypography'
import { notificationBodyWrapSx } from './notificationTextClamp'

type Props = {
  notification: Extract<KnownNotificationResource, { type: 'user_followed_author' }>
}

export function UserFollowedAuthorNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { onUserClick } = createNotificationNavHandlers(navigate)
  const payload = notification.payload
  const author = payload.author
  const lastFollower = payload.last_follower
  const authorName =
    typeof author?.fullname === 'string' && author.fullname.trim().length > 0 ? author.fullname : 'Ваш профиль'
  const authorAvatarSrc = mediaSourceUrl(author?.avatar) ?? undefined
  const authorPathKey = getActorPathKey(author)

  const followersCountRaw = Number(payload.followers_count ?? 0)
  const followersCount = Number.isFinite(followersCountRaw) ? Math.max(0, followersCountRaw) : 0
  const effectiveFollowersCount = followersCount > 0 ? followersCount : lastFollower ? 1 : 0

  const lastFollowerName =
    typeof lastFollower?.fullname === 'string' && lastFollower.fullname.trim().length > 0
      ? lastFollower.fullname
      : 'Пользователь'
  const followerPathKey = getActorPathKey(lastFollower, notification.actor_user_id)

  const followersPreview: IProjectAuthorShort[] = payload.followers_preview.map((follower) => ({
    id: follower.id,
    slug: null,
    fullname: follower.fullname,
    avatar: follower.avatar,
  }))

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
      <Stack direction="column" minWidth={0} flex={1} gap={1.25}>
        <Box
          onClick={(e) => onUserClick(e, authorPathKey)}
          sx={{ cursor: authorPathKey ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={authorName}
            avatarSrc={authorAvatarSrc}
            fallbackIcon={
              <Typography variant="black-14-normal" sx={{ color: 'inherit' }}>
                U
              </Typography>
            }
            subtitle="Новые подписчики"
          />
        </Box>

        <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
          {effectiveFollowersCount > 1 ? (
            <StackedAvatars users={followersPreview} size={34} overlap={10} />
          ) : (
            <Box
              onClick={followerPathKey ? (e) => onUserClick(e, followerPathKey) : undefined}
              sx={{ cursor: followerPathKey ? 'pointer' : 'default', flexShrink: 0 }}
            >
              <Avatar
                src={mediaSourceUrl(lastFollower?.avatar) ?? undefined}
                alt={lastFollowerName}
                sx={{ width: 34, height: 34 }}
              />
            </Box>
          )}
          <Stack minWidth={0}>
            {effectiveFollowersCount === 1 ? (
              <Typography variant="black-14-normal" sx={notificationBodyWrapSx}>
                На вас подписался(ась){' '}
                <ActorNameSpan pathKey={followerPathKey} onUserClick={onUserClick}>
                  {lastFollowerName}
                </ActorNameSpan>
              </Typography>
            ) : (
              <Typography variant="black-14-normal" sx={notificationBodyWrapSx}>
                На вас подписались {effectiveFollowersCount} человек
              </Typography>
            )}
            {effectiveFollowersCount > 1 && (
              <Typography variant="black-12-light" sx={{ opacity: 0.85, ...notificationBodyWrapSx }}>
                Последний подписчик:{' '}
                <ActorNameSpan pathKey={followerPathKey} onUserClick={onUserClick}>
                  {lastFollowerName}
                </ActorNameSpan>
              </Typography>
            )}
          </Stack>
        </Stack>

        <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
          {notification.read_at ? 'Прочитано' : 'Непрочитано'} • {new Date(notification.created_at).toLocaleString()}
        </Typography>
      </Stack>
    </Card>
  )
}
